import React, {
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import {
  MessageType,
  SESSION_SCOPE,
  SessionScope,
  SessionType,
} from 'models/Message';
import { useNavigate } from 'react-router-dom';
import { SessionCardPlaceholder } from './placeholders';
import EmptyScreen from 'components/EmptyScreen';
import PencilaltIcon from 'icons/Pencilalt.icon';
import SearchIcon from 'icons/Search.icon';
import SessionCard from './components/SessionCard';
import CheckToggle from 'components/CheckToggle';
import InfiniteScroll from 'components/InfiniteScroll';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import useChatListener from '../../pages/Chat/hooks/useChatListeners';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useInfinitePagination from 'hooks/useInfinitePagination';
import useEffectOnceWhen from 'hooks/useEffectOnce';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import useCallbackRef from 'hooks/useCallbackRef';
import useMatchMedia from 'hooks/useMatchMedia';
import { CallbackBusType, EventName } from 'modules/bus';
import { START_SCROLL_CONFIGURATION } from 'data/global';
import uniqBy from 'lodash/uniqBy';
import api from 'api';
import { getArrayLength, getHasElementsInArray, getIsArrayEmpty } from 'utils';
import useMessagingNavigation from '../../hooks/useMessagingNavigation';
import { MessageCreatedEventData } from 'services/socketService/socketEventData';
import useMarkSessionRead from '../../hooks/useMarkSessionRead';
import { getIsSessionListFiltered } from './utils';

import './MessagingContacts.styles.scss';

const NUMBER_OF_PLACEHOLDERS = {
  INITIAL_LOADING_COUNT: 6,
  PAGINATED_LOADING_COUNT: 2,
} as const;

const SCOPE_OPTIONS = [
  { value: SESSION_SCOPE.ALL, labelId: 'all' },
  { value: SESSION_SCOPE.MINE, labelId: 'mine' },
] as const;

type MessagingContactsProps = {
  className?: string;
  onNewMessage: () => void;
};

const MessagingContacts: React.FC<MessagingContactsProps> = (props) => {
  const { className, onNewMessage } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { currentUser, isSuperAdmin, markSessionUnread, updatePreferences } =
    useContext(CurrentUserContext);
  const {
    sessionId,
    currentSession,
    messagingPortfolioProps,
    setContactsOpen,
  } = useContext(MessagingContext);

  const classes = classNames('messaging-contacts', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const [sessionsScroll, sessionsScrollRef] =
    useCallbackRef<InfiniteScrollRef>();

  // The unread toggle always starts off, the segment remembers its last value.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [sessionScope, setSessionScope] = useState<SessionScope>(
    currentUser?.messagingSessionsScope || SESSION_SCOPE.ALL,
  );

  const onSessionScopeChange = useCallback(
    (messagingSessionsScope: SessionScope) => {
      setSessionScope(messagingSessionsScope);
      updatePreferences({ messagingSessionsScope });
    },
    [updatePreferences],
  );

  // Only Boss Mode sees third party conversations, so only it can narrow them away.
  const isOwnScopeOnly = isSuperAdmin && sessionScope === SESSION_SCOPE.MINE;

  // unreadSessionIds only covers the conversations the user takes part in, so
  // Boss Mode on "All" needs the platform wide count instead.
  const unreadCount =
    isSuperAdmin && !isOwnScopeOnly
      ? currentUser?.allUnreadSessionsCount || 0
      : getArrayLength(currentUser?.unreadSessionIds);

  const scopeCounts = {
    [SESSION_SCOPE.ALL]: currentUser?.allSessionsCount || 0,
    [SESSION_SCOPE.MINE]: currentUser?.mineSessionsCount || 0,
  };

  // Nothing to narrow down to with no unread conversations, but a toggle that
  // is already on stays usable so the user is never stuck on an empty list.
  const isUnreadDisabled = !unreadOnly && !unreadCount;

  const {
    loading,
    currentPage,
    searchString,
    items: sessions,
    setSearchString,
    setItems: setSessions,
    onContainerScrolled: onSessionScroll,
  } = useInfinitePagination<SessionType>({
    resetDeps: [unreadOnly, isOwnScopeOnly],
    dependencies: [unreadOnly, isOwnScopeOnly],
    makeRequest: async (currentPage: number, searchString: string) => {
      return api.messages
        .getSessions({
          $page: currentPage.toString(),
          $limit: '10',
          $sort: '-latestMessageAt',
          $searchString: searchString?.trim(),
          // openSessionId pulls the open session into the results, which would
          // put it back in a list the filters just took it out of.
          ...(!getIsSessionListFiltered(
            unreadOnly,
            isOwnScopeOnly,
            searchString,
          ) && { openSessionId: sessionId }),
          listingId: messagingPortfolioProps?.listingId,
          ...(unreadOnly && { hasUnreadMessages: true }),
          ...(isOwnScopeOnly && { isExternalCommunication: false }),
        })
        .then((res) => res.data);
    },
  });

  // The list no longer holds every conversation, so it must not be topped up
  // with sessions that the filters exclude.
  const isFiltered = getIsSessionListFiltered(
    unreadOnly,
    isOwnScopeOnly,
    searchString,
  );

  // A filter change refetches from page one, so the cards left over from the
  // previous scope go right away rather than lingering under the placeholders,
  // and the list starts at the top instead of wherever it was scrolled to.
  useEffectSkipFirst(() => {
    setSessions([]);
    sessionsScroll?.container?.scrollTo(START_SCROLL_CONFIGURATION);
  }, [unreadOnly, isOwnScopeOnly]);

  const getNavigationLink = useMessagingNavigation();

  const finalSessions = useMemo(
    () => sessions && uniqBy(sessions, '_id'),
    [sessions],
  );

  const numOfPlaceholders = useMemo(
    () =>
      currentPage > 1
        ? NUMBER_OF_PLACEHOLDERS.PAGINATED_LOADING_COUNT
        : NUMBER_OF_PLACEHOLDERS.INITIAL_LOADING_COUNT,
    [currentPage],
  );

  const emptyPlaceholder = useMemo(
    () => (
      <div className="messaging-contacts__placeholder">
        {loading &&
          Array(numOfPlaceholders)
            .fill(1)
            .map((_, i) => <SessionCardPlaceholder key={i} />)}
      </div>
    ),
    [loading, numOfPlaceholders],
  );

  const updateSession = useCallback(
    (sessionId: string, updatedSessionValues: Partial<SessionType>) =>
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          sessionId !== session._id
            ? session
            : { ...session, ...updatedSessionValues },
        ),
      ),
    [setSessions],
  );

  const markSessionAsUnread = useCallback(
    async (sid: string) => {
      try {
        await api.messages.unreadAllMessages(sid);

        markSessionUnread(sid);

        updateSession(sid, { hasUnreadMessages: true });
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError, updateSession, markSessionUnread],
  );

  const markSessionRead = useMarkSessionRead();

  const markSessionAsRead = useCallback(
    (sid: string, isExternalCommunication: boolean) => {
      // Opening a third party conversation does not read it, so its card keeps
      // the unread dot. useMarkSessionRead skips the request for the same
      // reason, and the optimistic update has to skip with it.
      if (isExternalCommunication) return;

      // optimistic: drop the card state before the API resolves
      updateSession(sid, { hasUnreadMessages: false });
      markSessionRead(sid, isExternalCommunication);
    },
    [updateSession, markSessionRead],
  );

  const onSessionPicked = useCallback(
    (data: CallbackBusType<SessionType>) => {
      const { payload: session } = data;

      navigate(getNavigationLink(session._id), {
        replace: true,
      });

      setSessions((oldSessions) => uniqBy([session, ...oldSessions], '_id'));
    },
    [navigate, getNavigationLink, setSessions],
  );

  const onMessageArrived = useCallback(
    ({ message, session }: MessageCreatedEventData) => {
      const { sessionId: msgSessionId } = message;

      const {
        _id,
        isDirectMessage,
        interlocutor,
        isGroupChat,
        groupName,
        isExternalCommunication,
        isClosed,
        leadWithdrawnInterest,
        isArchivedAfterMerging,
        deal,
        isRepresentingSellSide,
        sellRepresentative,
        buyRepresentatives,
        latestMessageAt,
      } = session;

      const isOpenSession = sessionId === msgSessionId;

      // only the open chat is read on arrival, and never a third party one
      const staysUnread = !isOpenSession || isExternalCommunication;

      // provider adds the unread session id globally on message-created;
      // if it's the open chat, read it right away (optimistically drops the id)
      if (isOpenSession)
        markSessionAsRead(msgSessionId, isExternalCommunication);

      // a conversation just read is out of scope under the unread filter, and a
      // third party one is out of scope under "Mine"
      if (
        (unreadOnly && !staysUnread) ||
        (isOwnScopeOnly && isExternalCommunication) ||
        searchString
      )
        return;

      setSessions((oldSessions) => {
        const existingSession = oldSessions.find((s) => s._id === msgSessionId);

        return [
          existingSession
            ? {
                ...existingSession,
                hasUnreadMessages: staysUnread,
                latestMessageAt,
              }
            : ({
                _id,
                isDirectMessage,
                interlocutor,
                isGroupChat,
                groupName,
                isExternalCommunication,
                isClosed,
                leadWithdrawnInterest,
                isArchivedAfterMerging,
                isRepresentingSellSide,
                sellRepresentative,
                buyRepresentatives,
                latestMessageAt,
                hasUnreadMessages: staysUnread,
                ...(deal && {
                  deal: {
                    name: deal.name,
                    portfolioName: deal.portfolioName,
                    ...(deal.listing && {
                      listing: {
                        listingTitle: deal.listing.listingTitle,
                        gallery: deal.listing.gallery,
                      },
                    }),
                  },
                }),
              } as SessionType),
          ...oldSessions.filter((session) => session._id !== msgSessionId),
        ];
      });
    },
    [
      sessionId,
      unreadOnly,
      isOwnScopeOnly,
      searchString,
      markSessionAsRead,
      setSessions,
    ],
  );

  const messageSentHandler = useCallback(
    async (data: { event: EventName; payload: MessageType }) => {
      const { sessionId } = data.payload;

      setSessions((oldSessions) => {
        const session = oldSessions.find((s) => s._id === sessionId);

        // the session the message went to can be missing because the filters
        // exclude it, and sending a message is no reason to put it back
        if (!session)
          return isFiltered
            ? oldSessions
            : [
                {
                  ...currentSession,
                  latestMessageAt: new Date(),
                },
                ...oldSessions,
              ];

        // read all messages from session only if there are unread messages
        if (session.hasUnreadMessages)
          markSessionAsRead(sessionId, session.isExternalCommunication);

        return oldSessions
          .map((session) =>
            session._id === sessionId
              ? {
                  ...session,
                  latestMessageAt: new Date(),
                }
              : session,
          )
          .sort((first, second) => {
            const firstDate = new Date(first.latestMessageAt).getTime();
            const secondDate = new Date(second.latestMessageAt).getTime();

            return firstDate > secondDate ? -1 : 1;
          });
      });
    },
    [currentSession, isFiltered, markSessionAsRead, setSessions],
  );

  useChatListener({
    onMessageArrived,
    onMessageSent: messageSentHandler,
    onSessionPicked,
  });

  useEffectOnceWhen(() => {
    if (sessionId) return;

    navigate(getNavigationLink(sessions[0]._id), { replace: true });
  }, getHasElementsInArray(sessions));

  useEffect(() => {
    // a deep linked session is pulled into the list so it can be highlighted,
    // unless the filters are what left it out
    if (
      isFiltered ||
      !sessionId ||
      sessions?.some(({ _id }) => _id === sessionId)
    )
      return;

    (async () => {
      try {
        const { data } = await api.messages.getSessionById(sessionId);
        setSessions((prev) => [data, ...prev]);
      } catch (e) {
        popServerError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    const { _id, isExternalCommunication, hasUnreadMessages } =
      currentSession || {};
    if (hasUnreadMessages && sessionId === _id) {
      markSessionAsRead(sessionId, isExternalCommunication);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, sessionId]);

  return (
    <div className={classes}>
      <div className="messaging-contacts__header">
        <div className="messaging-contacts__actions">
          <p className="messaging-contacts__actions__title">{t('messaging')}</p>
          <Button
            className="contact"
            variant="outline"
            icon={PencilaltIcon}
            onClick={onNewMessage}
          >
            {t('composeMessage')}
          </Button>
        </div>

        <Input
          className="messaging-contacts__search"
          value={searchString}
          prefixNode={<SearchIcon />}
          placeholder={t('searchByNameListingOrDeal')}
          onChange={(e) => setSearchString(e.target.value)}
        />

        <div className="messaging-contacts__filters">
          {isSuperAdmin && (
            <div className="messaging-contacts__filters__scope">
              {SCOPE_OPTIONS.map(({ value, labelId }) => (
                <Button
                  key={value}
                  variant="outline"
                  className={classNames({ selected: sessionScope === value })}
                  onClick={() => onSessionScopeChange(value)}
                >
                  {`${t(labelId)} (${scopeCounts[value]})`}
                </Button>
              ))}
            </div>
          )}

          <div
            className={classNames('messaging-contacts__filters__unread', {
              'messaging-contacts__filters__unread--disabled': isUnreadDisabled,
            })}
          >
            <CheckToggle
              checked={unreadOnly}
              disabled={isUnreadDisabled}
              onChange={({ target: { checked } }) => setUnreadOnly(checked)}
            />
            <p>{`${t('unreadOnly')} (${unreadCount})`}</p>
          </div>
        </div>
      </div>

      <InfiniteScroll
        localScroll
        ref={sessionsScrollRef}
        className="messaging-contacts__sessions"
        onScroll={onSessionScroll}
      >
        {!loading && getIsArrayEmpty(sessions) ? (
          unreadOnly ? (
            <EmptyScreen
              className="messaging-contacts__empty-screen"
              title={t('noUnreadConversations')}
              content={t('turnOffTheUnreadFilterToSeeAllOfThem')}
            />
          ) : (
            <EmptyScreen
              className="messaging-contacts__empty-screen"
              title={t(
                searchString
                  ? 'thereAreNoContactsForSearchedUser'
                  : 'youHaveNoContactsRightNow',
              )}
              {...(!searchString && {
                content: t('participantsForListingAreGoingToBecamaAvailable'),
              })}
            />
          )
        ) : (
          finalSessions?.map((session) => (
            <SessionCard
              key={session._id}
              session={session}
              markSessionAsUnread={markSessionAsUnread}
              onClick={() => {
                if (isPhablet) setContactsOpen(false);
              }}
            />
          ))
        )}

        {emptyPlaceholder}
      </InfiniteScroll>
    </div>
  );
};

export default MessagingContacts;
